// Shared Documents presentation for the drawer Media tab and the Updates
// Media panel: add-link entry with editable title, rows with type icon, key
// star, preview and remove, retained uploaded PDFs, and the embedded Google
// preview with an always-available external link. Historical non-Google URLs
// are never framed.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { parseGoogleDocument } from "../../../lib/projectDocumentLinks.js";
import { fmtIsoDate } from "../weekly/weeklyModel.js";
import { safeMediaUrl } from "../media/MediaStrip.jsx";
import { displayCaption } from "../media/mediaDisplay.js";
import { trustedGoogle, buildDocumentPayload, historicalExternalUrl } from "./documentPayload.js";
import { MAX_KEY_DOCUMENTS } from "./useProjectDocuments.js";

const ICON = {
  doc: ["DOC", T.blue], sheet: ["XLS", T.green], slide: ["PPT", T.pendingAmber], pdf: ["PDF", T.red],
  folder: ["DIR", T.mid], other: ["FILE", T.mid],
};
const STAR_ON = T.projectsReferenceGold;
const link = { color: T.blue, fontWeight: T.fwSemibold, fontSize: T.fsXs, background: "transparent", border: 0, padding: 0, cursor: "pointer", textDecoration: "none", fontFamily: "inherit", whiteSpace: "nowrap" };
const btnBase = { border: `1px solid ${T.borderDark}`, background: T.card, borderRadius: 8, padding: "6px 12px", fontWeight: T.fwSemibold, fontSize: T.fsXs, cursor: "pointer", color: T.dark, fontFamily: "inherit" };
const btnPri = { ...btnBase, background: T.blue, borderColor: T.blue, color: T.white };
const input = { flex: 1, minWidth: 0, border: `1px solid ${T.borderDark}`, borderRadius: 8, padding: "7px 10px", fontSize: T.fsXs, fontFamily: "inherit", boxSizing: "border-box" };
export const DOCUMENT_ROWS_CSS = `
.pd-documents { container: project-documents / inline-size; }
@container project-documents (max-width: 360px) {
  .pd-documents .pd-row { grid-template-columns: 26px minmax(0, 1fr) 26px !important; align-items: start !important; }
  .pd-documents .pd-uploaded-row { grid-template-columns: 26px minmax(0, 1fr) !important; }
  .pd-documents .pd-actions { grid-column: 2 / -1; flex-wrap: wrap; min-width: 0; }
  .pd-documents .pd-title { white-space: normal !important; overflow-wrap: anywhere; }
}
`;

const monD = (iso) => (fmtIsoDate(iso) || "").replace(/, \d{4}$/, "");

/** Type is the stored type when the URL is trusted, else honest "other". */
export function documentType(doc) {
  const parsed = trustedGoogle(doc);
  if (!parsed) return "other";
  return ICON[doc?.type] ? doc.type : parsed.type || "other";
}

export function FileIcon({ type }) {
  const [text, bg] = ICON[type] || ICON.other;
  return <span aria-hidden="true" data-testid={`doc-icon-${type}`} style={{ width: 22, height: 26, borderRadius: 3, display: "inline-flex", alignItems: "flex-end", justifyContent: "center", fontSize: T.fsXxxs, fontWeight: T.fwBold, color: T.white, paddingBottom: 2, flex: "none", background: bg, boxSizing: "border-box" }}>{text}</span>;
}

export function documentMeta(doc) {
  const date = monD(doc?.addedAt);
  const who = typeof doc?.addedByName === "string" && doc.addedByName.trim() ? doc.addedByName.trim() : null;
  if (who) return `added by ${who}${date ? ` · ${date}` : ""}`;
  return `added by unknown${date ? ` · ${date}` : ""}`;
}

export function documentTitle(doc) {
  return (typeof doc?.title === "string" && doc.title.trim()) || doc?.source || "Untitled document";
}

// Key chips: only trusted Google links are shown; legacy rows never become chips.
export function KeyDocumentChips({ docs, onOpen, testId = "key-documents", manageLabel = null, onManage }) {
  const chips = (docs.keys || []).filter((d) => trustedGoogle(d)).slice(0, MAX_KEY_DOCUMENTS);
  if (docs.status !== "ok" || chips.length === 0) return null;
  return (
    <section data-testid={testId} aria-label="Key documents">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>Key documents</h3>
        {manageLabel && <button type="button" data-testid={`${testId}-manage`} style={link} onClick={onManage}>{manageLabel}</button>}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {chips.map((d) => (
          <button key={d.id} type="button" data-testid={`${testId}-chip-${d.id}`} onClick={() => onOpen(d)}
            style={{ display: "flex", alignItems: "center", gap: 8, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px", background: T.card, cursor: "pointer", fontFamily: "inherit", fontSize: T.fsXs, color: T.dark, maxWidth: "100%" }}>
            <FileIcon type={documentType(d)} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{documentTitle(d)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function Preview({ doc, onClose }) {
  const parsed = trustedGoogle(doc);
  const [loaded, setLoaded] = useState(false);
  const [refused, setRefused] = useState(false);
  useEffect(() => { setLoaded(false); setRefused(false); }, [doc?.id]);
  // Only detectable refusals: frame error event, or a CSP violation for this
  // exact frame origin. Opaque cross-origin outcomes are never "verified".
  useEffect(() => {
    if (!parsed?.previewUrl || typeof document === "undefined") return undefined;
    const origin = new URL(parsed.previewUrl).origin;
    const onViolation = (e) => {
      const blocked = typeof e?.blockedURI === "string" ? e.blockedURI : "";
      if (/frame-src|child-src/.test(e?.effectiveDirective || e?.violatedDirective || "") && (blocked === origin || blocked.startsWith(`${origin}/`))) setRefused(true);
    };
    document.addEventListener("securitypolicyviolation", onViolation);
    return () => document.removeEventListener("securitypolicyviolation", onViolation);
  }, [parsed?.previewUrl]);
  const external = parsed?.url || null;
  return (
    <div data-testid="document-preview" style={{ marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: `1px solid ${T.border}`, background: T.hoverBg }}>
        <b style={{ fontSize: T.fsXs, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{documentTitle(doc)}</b>
        <span style={{ display: "inline-flex", gap: 12, flex: "none" }}>
          {external && <a href={external} target="_blank" rel="noopener noreferrer" data-testid="document-preview-open" style={link}>Open in Drive ↗</a>}
          <button type="button" data-testid="document-preview-close" style={{ ...link, color: T.mid }} onClick={onClose}>Close</button>
        </span>
      </div>
      {parsed?.previewUrl && !refused ? (
        <>
          <iframe title={`Preview of ${documentTitle(doc)}`} data-testid="document-preview-frame" src={parsed.previewUrl}
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" referrerPolicy="no-referrer"
            onLoad={() => setLoaded(true)} onError={() => setRefused(true)} style={{ display: "block", width: "100%", height: 360, border: 0, background: T.card }} />
          <div role="note" style={{ padding: "6px 12px", fontSize: T.fsXxs, color: T.mid, borderTop: `1px solid ${T.border}` }}>
            {loaded ? "Drive's own viewer. If it shows a sign-in or access message, use Open in Drive." : "Loading Drive's viewer. You see it only if Drive gives you access; otherwise use Open in Drive."}
          </div>
        </>
      ) : (
        <div data-testid="document-preview-unavailable" style={{ padding: "18px 16px", fontSize: T.fsXs, color: T.mid }}>
          {refused ? "The preview was blocked in this browser. Use Open in Drive." : external ? "This link has no embeddable preview (for example, a folder). Use Open in Drive." : "This historical link is not a Google Drive link, so it is not previewed here."}
        </div>
      )}
    </div>
  );
}

/**
 * docs: result of useProjectDocuments. pdfItems: retained uploaded PDFs
 * (media rows). readOnly forces viewer mode regardless of access.
 */
export const ProjectDocuments = forwardRef(function ProjectDocuments({ docs, pdfItems = [], readOnly = false, selectedId = null, onSelect, onDraftState, testId = "project-documents" }, ref) {
  const canManage = docs.canManage && !readOnly;
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [titleTouched, setTitleTouched] = useState(false);
  const [formError, setFormError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [localSel, setLocalSel] = useState(null);
  const urlRef = useRef(null);
  const sel = selectedId ?? localSel;
  const select = (id) => { if (onSelect) onSelect(id); else setLocalSel(id); };
  const parsed = parseGoogleDocument(url);
  // Unsent link/title drafts and in-flight writes participate in the owner's
  // record leave guard (drawer close/switch/navigation).
  const draftCb = useRef(onDraftState);
  draftCb.current = onDraftState;
  const draftDirty = Boolean(url.trim() || (titleTouched && title.trim()));
  const writing = docs.busyId != null;
  useEffect(() => { draftCb.current?.({ editing: draftDirty, busy: writing }); }, [draftDirty, writing]);
  useEffect(() => () => draftCb.current?.({ editing: false, busy: false }), []);
  useImperativeHandle(ref, () => ({ focusAdd: () => urlRef.current?.focus() }), []);

  const changeUrl = (value) => {
    setUrl(value); setFormError(null);
    const p = parseGoogleDocument(value);
    if (!titleTouched) setTitle(p?.title || "");
  };
  const submit = async (event) => {
    event.preventDefault();
    const built = buildDocumentPayload({ url, title }, true);
    if (built.error) { setFormError(built.error); return; }
    if (await docs.add(built.payload)) { setUrl(""); setTitle(""); setTitleTouched(false); setFormError(null); }
  };
  const replace = (item) => {
    setTitle(displayCaption(item) || ""); setTitleTouched(true); setUrl(""); setFormError(null);
    urlRef.current?.focus();
  };
  const selected = docs.items.find((d) => String(d.id) === String(sel)) || null;
  const busy = docs.busyId != null;
  const total = docs.items.length + pdfItems.length;

  return (
    <div className="pd-documents" data-testid={testId}>
      <style>{DOCUMENT_ROWS_CSS}</style>
      {canManage && (
        <form onSubmit={submit} data-testid="document-add-form" style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <input ref={urlRef} aria-label="Google Drive link" data-testid="input-document-url" placeholder="Paste a Google Drive link (Docs, Sheets, Slides, PDF)…" value={url} disabled={busy} onChange={(e) => changeUrl(e.target.value)} style={input} />
            <button type="submit" data-testid="button-document-add" disabled={busy || !url.trim()} style={{ ...btnPri, opacity: busy || !url.trim() ? 0.55 : 1 }}>{docs.busyId === "new" ? "Adding…" : "Add"}</button>
          </div>
          {(url.trim() || titleTouched) && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, fontSize: T.fsXs, color: T.mid }}>
              Title
              <input aria-label="Document title" data-testid="input-document-title" required value={title} disabled={busy} placeholder="Name this document" onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); setFormError(null); }} style={input} />
            </label>
          )}
          {url.trim() && !parsed && <div data-testid="document-url-hint" style={{ fontSize: T.fsXxs, color: T.mid, marginTop: 4 }}>Only https://drive.google.com and https://docs.google.com links are accepted.</div>}
          {formError && <div role="alert" data-testid="document-form-error" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{formError}</div>}
        </form>
      )}
      {docs.error && <div role="alert" data-testid="document-action-error" style={{ color: T.red, fontSize: T.fsXs, marginBottom: 8 }}>{docs.error}</div>}
      {docs.status === "loading" && <div aria-busy="true" data-testid="documents-loading">{[0, 1, 2].map((i) => <div key={i} style={{ height: 34, borderRadius: 6, background: T.lightGray, marginBottom: 8 }} />)}</div>}
      {docs.status === "error" && <div role="alert" style={{ color: T.red, fontSize: T.fsXs }}>Couldn't load documents: {docs.error || "request failed"} <button type="button" style={btnBase} data-testid="button-documents-retry" onClick={docs.retry}>Retry</button></div>}
      {docs.status === "ok" && total === 0 && <p data-testid="documents-empty" style={{ margin: "12px 0", color: T.mid, fontSize: T.fsXs }}>No documents linked yet.{canManage ? " Paste a Google Drive link above." : ""}</p>}
      {docs.status === "ok" && docs.items.map((d) => {
        const trusted = trustedGoogle(d);
        const historicalHref = !trusted ? historicalExternalUrl(d.url) : null;
        const type = documentType(d);
        const name = documentTitle(d);
        const rowManage = canManage && d.canManage !== false;
        return (
          <div className="pd-row" key={d.id} data-testid={`document-row-${d.id}`} style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr) auto auto", gap: 10, alignItems: "center", padding: "9px 0", borderTop: `1px solid ${T.border}` }}>
            <FileIcon type={type} />
            <div style={{ minWidth: 0 }}>
              <div className="pd-title" title={name} style={{ fontSize: T.fsXs, color: T.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
              <div data-testid={`document-meta-${d.id}`} style={{ fontSize: T.fsXxs, color: T.light }}>{documentMeta(d)}</div>
            </div>
            {rowManage && trusted ? (
              <button type="button" data-testid={`button-document-star-${d.id}`} aria-pressed={d.isKey === true} disabled={busy}
                aria-label={d.isKey ? `Remove ${name} from key documents` : `Mark ${name} as a key document`}
                onClick={() => docs.toggleKey(d)} style={{ border: 0, background: "transparent", cursor: busy ? "wait" : "pointer", fontSize: T.fsMd, color: d.isKey ? STAR_ON : T.light, padding: 0 }}>{d.isKey ? "★" : "☆"}</button>
            ) : d.isKey && trusted ? <span aria-label="Key document" style={{ fontSize: T.fsMd, color: STAR_ON }}>★</span> : <span />}
            <span className="pd-actions" style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
              {trusted
                ? <button type="button" data-testid={`button-document-preview-${d.id}`} style={link} aria-expanded={String(sel) === String(d.id)} onClick={() => select(String(sel) === String(d.id) ? null : d.id)}>Preview</button>
                : <><span data-testid={`document-legacy-${d.id}`} style={{ fontSize: T.fsXxs, color: T.mid }}>Historical link</span>
                  {historicalHref && <a data-testid={`document-external-${d.id}`} href={historicalHref} target="_blank" rel="noopener noreferrer" aria-label={`Open ${name} externally`} style={link}>Open ↗</a>}</>}
              {rowManage && (confirmRemove === d.id
                ? <span style={{ display: "inline-flex", gap: 6 }}>
                    <button type="button" data-testid={`button-document-remove-confirm-${d.id}`} disabled={busy} style={{ ...link, color: T.red }} onClick={async () => { if (await docs.remove(d)) { setConfirmRemove(null); if (String(sel) === String(d.id)) select(null); } }}>Remove</button>
                    <button type="button" data-testid={`button-document-remove-cancel-${d.id}`} style={{ ...link, color: T.mid }} onClick={() => setConfirmRemove(null)}>Keep</button>
                  </span>
                : <button type="button" data-testid={`button-document-remove-${d.id}`} aria-label={`Remove ${name}`} disabled={busy} style={{ ...link, color: T.mid }} onClick={() => setConfirmRemove(d.id)}>Remove</button>)}
            </span>
          </div>
        );
      })}
      {docs.status === "ok" && pdfItems.map((item) => {
        const href = safeMediaUrl(item.downloadUrl || item.url);
        return (
          <div className="pd-row pd-uploaded-row" key={`pdf-${item.id}`} data-testid={`uploaded-pdf-row-${item.id}`} style={{ display: "grid", gridTemplateColumns: "26px minmax(0,1fr) auto", gap: 10, alignItems: "center", padding: "9px 0", borderTop: `1px solid ${T.border}` }}>
            <FileIcon type="pdf" />
            <div style={{ minWidth: 0 }}>
              <div className="pd-title" style={{ fontSize: T.fsXs, color: T.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayCaption(item)}</div>
              <div style={{ fontSize: T.fsXxs, color: T.light }}>Uploaded file{item.createdAt ? ` · ${monD(item.createdAt)}` : ""}</div>
            </div>
            <span className="pd-actions" style={{ display: "inline-flex", gap: 10 }}>
              {href && <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`link-uploaded-pdf-${item.id}`} style={link}>Open ↗</a>}
              {canManage && <button type="button" data-testid={`button-replace-pdf-${item.id}`} style={{ ...link, color: T.mid, fontWeight: T.fwRegular }} onClick={() => replace(item)}>Replace with Drive link</button>}
            </span>
          </div>
        );
      })}
      {selected && <Preview doc={selected} onClose={() => select(null)} />}
    </div>
  );
});
